import React, { useEffect, useState } from "react";
import {
  Card, Col, DatePicker, Empty, Row, Select, Space, Spin, Table, Typography,
} from "antd";
import { UserOutlined, BarChartOutlined } from "@ant-design/icons";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const API_URL = process.env.REACT_APP_API_URL;
const DATE_FMT = "YYYY-MM-DD";

const toArr = v => Array.isArray(v?.$values) ? v.$values : Array.isArray(v) ? v : [];

export default function FrmLecheVaca() {
  const [vacas, setVacas] = useState([]);
  const [animalId, setAnimalId] = useState(null);
  const [rango, setRango] = useState([dayjs().subtract(29, "day"), dayjs()]);
  const [reporte, setReporte] = useState(null);
  const [cargando, setCargando] = useState(false);

  // Cargar lista de hembras activas
  useEffect(() => {
    axios.get(`${API_URL}/Animal`, {
      params: { sexo: "Hembra", estado: "Activo", pagina: 1, tamañoPagina: 200 },
    }).then(resp => {
      const lista = Array.isArray(resp.data?.lista?.$values)
        ? resp.data.lista.$values
        : Array.isArray(resp.data?.lista)
        ? resp.data.lista
        : [];
      setVacas(lista);
    }).catch(() => {});
  }, []);

  // Cargar reporte cuando cambia animal o rango
  useEffect(() => {
    if (!animalId || !rango || rango.length !== 2) return;
    const cargar = async () => {
      try {
        setCargando(true);
        const resp = await axios.get(`${API_URL}/Leche/vaca/${animalId}`, {
          params: {
            desde: rango[0].format(DATE_FMT),
            hasta: rango[1].format(DATE_FMT),
          },
        });
        const d = resp.data;
        setReporte({
          ...d,
          dias: toArr(d.dias).map(dia => ({
            ...dia,
            fechaLabel: dayjs(dia.fecha).format("DD/MM"),
            litros: Number(dia.litros ?? 0),
            ingreso: Number(dia.ingreso ?? 0),
          })),
        });
      } catch {
        setReporte(null);
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [animalId, rango]);

  const columnasDias = [
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      render: v => dayjs(v).format(DATE_FMT),
    },
    {
      title: "Litros",
      dataIndex: "litros",
      key: "litros",
      align: "right",
      render: v => <Text strong style={{ color: "#1677ff" }}>{Number(v).toFixed(2)} L</Text>,
    },
    {
      title: "Ingreso ($)",
      dataIndex: "ingreso",
      key: "ingreso",
      align: "right",
      render: v => <Text strong style={{ color: "#52c41a" }}>${Number(v).toLocaleString("es-CO")}</Text>,
    },
    {
      title: "Observación",
      dataIndex: "observacion",
      key: "observacion",
      ellipsis: true,
      render: v => v || <Text type="secondary">—</Text>,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>
        <BarChartOutlined style={{ marginRight: 8 }} />
        Producción por Vaca
      </Title>
      <Text type="secondary">
        Seleccione una vaca y un rango de fechas para ver su historial de producción e ingresos.
      </Text>

      <Card style={{ marginTop: 12, marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={10}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Vaca</Text>
              <Select
                showSearch
                placeholder="Seleccione una vaca..."
                style={{ width: "100%" }}
                value={animalId}
                onChange={setAnimalId}
                optionFilterProp="children"
                filterOption={(input, opt) =>
                  (opt?.children ?? "").toLowerCase().includes(input.toLowerCase())
                }
              >
                {vacas.map(v => (
                  <Option key={v.id} value={v.id}>
                    {v.arete} — {v.nombre || "Sin nombre"}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col xs={24} md={10}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Rango de fechas</Text>
              <RangePicker
                style={{ width: "100%" }}
                value={rango}
                onChange={val => val && setRango(val)}
                format={DATE_FMT}
                allowClear={false}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <Spin spinning={cargando}>
        {!animalId ? (
          <Empty description="Seleccione una vaca para ver su producción." />
        ) : !reporte || reporte.dias.length === 0 ? (
          !cargando && <Empty description="Sin registros de leche en el rango seleccionado." />
        ) : (
          <>
            {/* Tarjetas resumen */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Vaca</Text>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>
                    {reporte.arete} — {reporte.nombre || "—"}
                  </div>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Total litros</Text>
                  <div style={{ fontSize: 26, fontWeight: 700, color: "#1677ff" }}>
                    {Number(reporte.totalLitros ?? 0).toFixed(2)} L
                  </div>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Ingreso total</Text>
                  <div style={{ fontSize: 26, fontWeight: 700, color: "#52c41a" }}>
                    ${Number(reporte.ingresoTotal ?? 0).toLocaleString("es-CO")}
                  </div>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Promedio diario</Text>
                  <div style={{ fontSize: 26, fontWeight: 700 }}>
                    {Number(reporte.promedioDiario ?? 0).toFixed(2)} L
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Precio: ${Number(reporte.precioPorLitro ?? 0).toLocaleString("es-CO")}/L
                  </Text>
                </Card>
              </Col>
            </Row>

            {/* Gráfico de litros */}
            <Card title="Producción diaria (litros)" style={{ marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={reporte.dias} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorLitros" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1677ff" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1677ff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fechaLabel" />
                  <YAxis label={{ value: "Litros", angle: -90, position: "insideLeft" }} />
                  <Tooltip formatter={(v, n) => [Number(v).toFixed(2) + " L", n]} />
                  <Area
                    type="monotone"
                    dataKey="litros"
                    name="Litros"
                    stroke="#1677ff"
                    fill="url(#colorLitros)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            {/* Gráfico de ingresos */}
            {Number(reporte.precioPorLitro) > 0 && (
              <Card title="Ingreso diario ($)" style={{ marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={reporte.dias} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="fechaLabel" />
                    <YAxis />
                    <Tooltip formatter={v => [`$${Number(v).toLocaleString("es-CO")}`, "Ingreso"]} />
                    <Bar dataKey="ingreso" name="Ingreso ($)" fill="#52c41a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* Tabla de detalle */}
            <Card title="Detalle por día">
              <Table
                rowKey={r => r.fecha}
                columns={columnasDias}
                dataSource={reporte.dias}
                pagination={{ pageSize: 15 }}
                size="small"
                summary={pageData => {
                  const tL = pageData.reduce((s, r) => s + Number(r.litros || 0), 0);
                  const tI = pageData.reduce((s, r) => s + Number(r.ingreso || 0), 0);
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong style={{ color: "#1677ff" }}>{tL.toFixed(2)} L</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Text strong style={{ color: "#52c41a" }}>${tI.toLocaleString("es-CO")}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} />
                    </Table.Summary.Row>
                  );
                }}
              />
            </Card>
          </>
        )}
      </Spin>
    </div>
  );
}
